import type { QueryClient } from '@tanstack/react-query';

import { refreshSession } from './client';
import { getConnectivity, isOnline, reportUnreachable, subscribeConnectivity } from './connectivity';
import { sessionStore, type Session, type SessionUser } from './session';
import { purgeUserData } from './userData';

/**
 * Sesión sin conexión (PWA). El access token vive solo en memoria y el refresh en una cookie
 * httpOnly: al abrir la app sin señal la sesión no se puede renovar. Para que la persona vea sus
 * datos guardados (ruta, pasaporte, marcas del mapa) se recuerda QUIÉN entró por última vez en este
 * dispositivo (id y nombre visible, nunca tokens, teléfono ni correo) y, sin red, se abre una sesión
 * sin token con esa identidad. Con ella no se puede pedir nada al API: TanStack Query queda en pausa
 * hasta que vuelve la conexión y la sesión se renueva de verdad (o el API la rechaza y se olvida).
 */
export const IDENTITY_KEY = 'parche-cafetero:identidad:v1';
/** Prefijo de lo que se guarda por persona en este dispositivo (identidad y cola sin conexión). */
export const USER_STORAGE_PREFIX = 'parche-cafetero:';

/** `/readyz` responde 200 solo si el API puede atender (BD y Redis): distingue "sin red" de "sesión rechazada". */
const READY_URL = '/readyz';
const READY_TIMEOUT_MS = 5000;

export interface OfflineIdentity {
  id: string;
  displayName: string | null;
}

function localStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readIdentity(): OfflineIdentity | null {
  try {
    const raw = localStore()?.getItem(IDENTITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OfflineIdentity> | null;
    if (!parsed || typeof parsed.id !== 'string') return null;
    return { id: parsed.id, displayName: typeof parsed.displayName === 'string' ? parsed.displayName : null };
  } catch {
    return null;
  }
}

function writeIdentity(user: SessionUser): void {
  try {
    localStore()?.setItem(IDENTITY_KEY, JSON.stringify({ id: user.id, displayName: user.display_name ?? null }));
  } catch {
    // Sin almacenamiento (modo privado): simplemente no habrá sesión sin conexión
  }
}

/** Llave de algo guardado por persona (`parche-cafetero:<tipo>:<userId>`). */
export function userStorageKey(kind: string, userId: string): string {
  return `${USER_STORAGE_PREFIX}${kind}:${userId}`;
}

/**
 * Olvida quién entró en este dispositivo y lo que dejó guardado aparte de la caché (su cola sin
 * conexión). Se llama al cerrar sesión y cuando el API rechaza la sesión.
 */
export function forgetIdentity(): void {
  const store = localStore();
  if (!store) return;
  try {
    const identity = readIdentity();
    store.removeItem(IDENTITY_KEY);
    if (!identity) return;
    const suffix = `:${identity.id}`;
    for (let index = store.length - 1; index >= 0; index -= 1) {
      const key = store.key(index);
      if (key?.startsWith(USER_STORAGE_PREFIX) && key.endsWith(suffix)) store.removeItem(key);
    }
  } catch {
    // Sin almacenamiento: no hay nada que olvidar
  }
}

let offline = false;
const listeners = new Set<() => void>();

/** La sesión actual es la de sin conexión (sin token). */
export function isOfflineSession(): boolean {
  return offline;
}

export function subscribeOfflineSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setOffline(value: boolean): void {
  if (offline === value) return;
  offline = value;
  for (const listener of [...listeners]) listener();
}

/** Abre la sesión sin conexión con la identidad recordada (sin token: el API no se puede usar con ella). */
export function enterOfflineSession(identity: OfflineIdentity): void {
  const user: SessionUser = { id: identity.id, role: 'CLIENT', email: null, phone: null, display_name: identity.displayName };
  setOffline(true);
  sessionStore.set({ accessToken: '', tokenType: 'bearer', expiresAt: 0, user });
}

/** `true`: el API está listo · `false`: responde pero no puede atender · `null`: no hay red. */
export async function serverReady(): Promise<boolean | null> {
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), READY_TIMEOUT_MS) : null;
  try {
    const response = await fetch(READY_URL, { cache: 'no-store', signal: controller?.signal });
    return response.ok;
  } catch {
    return null;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

/**
 * No hay sesión (la renovación falló al abrir o se perdió después). Si el API está listo y aun así
 * la rechazó, la sesión ya no existe: se olvida la identidad y se borran de la caché los datos de esa
 * persona. Si no hay red o el API no puede atender, se abre la sesión sin conexión. Cuando el propio
 * API respondió que la sesión no vale (401/403 al renovar), ya se sabe que está disponible: no se prueba.
 */
export async function settleLostSession(queryClient: QueryClient): Promise<void> {
  const identity = readIdentity();
  if (!identity) return;
  const ready = sessionStore.wasRejected() ? true : await serverReady();
  // Alguien entró mientras se consultaba
  if (sessionStore.get()) return;
  if (ready === true) {
    forgetIdentity();
    purgeUserData(queryClient);
    return;
  }
  // Sin red (o el API no puede atender): se sondea hasta que vuelva y entonces se renueva la sesión.
  reportUnreachable();
  enterOfflineSession(identity);
}

/** Si hay identidad recordada, entra sin conexión con ella (la renovación sigue en segundo plano). */
export function enterOfflineSessionIfRemembered(): boolean {
  if (sessionStore.get()) return false;
  const identity = readIdentity();
  if (!identity) return false;
  enterOfflineSession(identity);
  return true;
}

let reconnecting: Promise<void> | null = null;

/** Volvió la conexión con la sesión sin conexión abierta: se renueva la sesión de verdad. */
export function reconnectOfflineSession(): Promise<void> {
  reconnecting ??= refreshSession()
    .then(() => undefined, () => undefined)
    .finally(() => {
      reconnecting = null;
    });
  return reconnecting;
}

const isClient = (session: Session | null) => session?.user.role === 'CLIENT';

/**
 * Sigue la sesión: recuerda a cada cliente que entra (y borra los datos de otra persona si cambió),
 * resuelve la sesión perdida y, al volver la conexión, renueva la sesión sin conexión.
 */
export function installOfflineSession(queryClient: QueryClient): () => void {
  let previous = sessionStore.get();
  const unsubscribeSession = sessionStore.subscribe(() => {
    const current = sessionStore.get();
    const before = previous;
    previous = current;
    if (current?.accessToken) {
      setOffline(false);
      if (current.user.role !== 'CLIENT') return;
      const known = readIdentity();
      if (known && known.id !== current.user.id) purgeUserData(queryClient);
      writeIdentity(current.user);
      return;
    }
    // La sesión sin conexión que se acaba de abrir
    if (current) return;
    const wasOffline = offline;
    setOffline(false);
    if (isClient(before) && (before?.accessToken || wasOffline)) void settleLostSession(queryClient);
  });
  const unsubscribeNetwork = subscribeConnectivity(() => {
    if (offline && isOnline(getConnectivity())) void reconnectOfflineSession();
  });
  return () => {
    unsubscribeSession();
    unsubscribeNetwork();
  };
}

/** Solo para pruebas. */
export function resetOfflineSession(): void {
  setOffline(false);
  reconnecting = null;
}
